import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { User, Mail, Phone, Camera, Save, Key, Shield, Calendar, CreditCard, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Helper to build full image URL - handles /api/uploads paths
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // URLs starting with /api/uploads or /uploads should use the API base
  if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) {
    const baseUrl = API.replace('/api', '');
    // Ensure we have /api/uploads in the path
    const path = url.startsWith('/api/') ? url : `/api${url}`;
    return `${baseUrl}${path}`;
  }
  return url;
};

const ROLE_NAMES = {
  admin: 'Administrador',
  coordenador: 'Coordenador',
  lider_pg: 'Líder de PG',
  facilitador: 'Facilitador',
  voluntario: 'Voluntário',
  visualizador: 'Visualizador',
};

const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-800',
  coordenador: 'bg-purple-100 text-purple-800',
  lider_pg: 'bg-blue-100 text-blue-800',
  facilitador: 'bg-cyan-100 text-cyan-800',
  voluntario: 'bg-green-100 text-green-800',
  visualizador: 'bg-gray-100 text-gray-800',
};

const Profile = () => {
  const { user, login, token, permissions } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [volunteerData, setVolunteerData] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    photo_url: '',
    // Volunteer fields
    cpf: '',
    birth_date: '',
    gender: '',
    volunteer_email: ''
  });
  const [serviceHistory, setServiceHistory] = useState([]);
  const [loadingServiceHistory, setLoadingServiceHistory] = useState(false);
  
  // Password change
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUserData(response.data);
      setVolunteerData(response.data.volunteer);
      setFormData({
        name: response.data.user?.name || '',
        phone: response.data.user?.phone || '',
        photo_url: response.data.user?.photo_url || '',
        // Volunteer fields
        cpf: response.data.volunteer?.cpf || '',
        birth_date: response.data.volunteer?.birth_date || '',
        gender: response.data.volunteer?.gender || '',
        volunteer_email: response.data.volunteer?.email || ''
      });

      const volunteerId = response.data.volunteer?.id;
      if (volunteerId) {
        setLoadingServiceHistory(true);
        try {
          const historyRes = await axios.get(`${API}/volunteers/${volunteerId}/service-history`);
          setServiceHistory(historyRes.data.history || []);
        } catch {
          setServiceHistory([]);
        } finally {
          setLoadingServiceHistory(false);
        }
      }
    } catch (error) {
      toast.error('Erro ao carregar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that have actual values (not empty strings)
      const dataToSend = {};
      if (formData.name) dataToSend.name = formData.name;
      if (formData.phone) dataToSend.phone = formData.phone;
      if (formData.photo_url) dataToSend.photo_url = formData.photo_url;
      // Volunteer fields (only if user has volunteer linked)
      if (volunteerData) {
        if (formData.cpf) dataToSend.cpf = formData.cpf;
        if (formData.birth_date) dataToSend.birth_date = formData.birth_date;
        if (formData.gender) dataToSend.gender = formData.gender;
        if (formData.volunteer_email) dataToSend.volunteer_email = formData.volunteer_email;
      }
      
      const response = await axios.put(`${API}/auth/profile`, dataToSend);
      
      // Update local user data
      const updatedUser = { ...user, ...dataToSend };
      login(token, updatedUser, permissions);
      
      toast.success('Perfil atualizado!');
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar perfil');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('As senhas não conferem');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordDialogOpen(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('A imagem deve ter no máximo 5MB');
      return;
    }
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/photo`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, photo_url: response.data.url });
      toast.success('Foto enviada! Clique em Salvar para confirmar.');
    } catch (error) {
      toast.error('Erro ao enviar foto');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  return (
    <div className="fade-in max-w-3xl mx-auto">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Meu Perfil</h1>
      <p className="text-gray-500 mb-8">Visualize e atualize suas informações pessoais</p>

      <div className="grid gap-6">
        {/* Profile Card */}
        <div className="card">
          <div className="flex items-start gap-6">
            {/* Photo */}
            <div className="relative">
              <div 
                className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-3xl font-bold overflow-hidden cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.photo_url ? (
                  <img src={getImageUrl(formData.photo_url)} alt="Foto" className="w-full h-full object-cover" />
                ) : (
                  formData.name?.charAt(0).toUpperCase() || 'U'
                )}
              </div>
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute bottom-0 right-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center hover:bg-blue-600 transition"
              >
                <Camera size={16} />
              </button>
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </div>
            
            {/* Info */}
            <div className="flex-1">
              <h2 className="text-xl font-bold">{userData?.user?.name}</h2>
              <p className="text-gray-500">{userData?.user?.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[userData?.user?.role] || 'bg-gray-100'}`}>
                  <Shield size={12} className="inline mr-1" />
                  {ROLE_NAMES[userData?.user?.role] || userData?.user?.role}
                </span>
                {userData?.user?.is_active ? (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Ativo
                  </span>
                ) : (
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Inativo
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Form */}
        <div className="card">
          <h3 className="font-semibold text-lg mb-4">Informações Pessoais</h3>
          
          <div className="grid gap-4">
            <div>
              <Label>Nome Completo</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="pl-10"
                  placeholder="Seu nome"
                  data-testid="profile-name-input"
                />
              </div>
            </div>
            
            <div>
              <Label>Email de Login</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  value={userData?.user?.email || ''}
                  disabled
                  className="pl-10 bg-gray-50"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">O email de login não pode ser alterado</p>
            </div>
            
            <div>
              <Label>Telefone</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="pl-10"
                  placeholder="(00) 00000-0000"
                  data-testid="profile-phone-input"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end mt-6">
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="profile-save-btn">
              <Save size={16} className="mr-2" />
              {saving ? 'Salvando...' : 'Salvar Alterações'}
            </Button>
          </div>
        </div>

        {/* Volunteer Data - Only show if user has linked volunteer */}
        {volunteerData && (
          <div className="card">
            <h3 className="font-semibold text-lg mb-4">
              <Users size={18} className="inline mr-2" />
              Dados Cadastrais
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Informações do seu cadastro como voluntário no ministério.
            </p>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>CPF</Label>
                <div className="relative">
                  <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    value={formData.cpf}
                    onChange={(e) => setFormData({ ...formData, cpf: e.target.value })}
                    className="pl-10"
                    placeholder="000.000.000-00"
                    data-testid="profile-cpf-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Data de Nascimento</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="date"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="pl-10"
                    data-testid="profile-birthdate-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Gênero</Label>
                <Select 
                  value={formData.gender} 
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger data-testid="profile-gender-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="masculino">Masculino</SelectItem>
                    <SelectItem value="feminino">Feminino</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Email de Contato</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    type="email"
                    value={formData.volunteer_email}
                    onChange={(e) => setFormData({ ...formData, volunteer_email: e.target.value })}
                    className="pl-10"
                    placeholder="seu@email.com"
                    data-testid="profile-volunteer-email-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Email para contato no ministério (pode ser diferente do email de login)</p>
              </div>
            </div>
            
            <div className="flex justify-end mt-6">
              <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="profile-volunteer-save-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Salvando...' : 'Salvar Alterações'}
              </Button>
            </div>
          </div>
        )}

        {volunteerData && (
          <div className="card">
            <h3 className="font-semibold text-lg mb-4">
              <Calendar size={18} className="inline mr-2" />
              Histórico de Serviço em Escalas
            </h3>
            {loadingServiceHistory ? (
              <p className="text-sm text-gray-500">Carregando histórico...</p>
            ) : serviceHistory.length === 0 ? (
              <p className="text-sm text-gray-500">Nenhum registro de serviço em escala.</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {serviceHistory.slice(0, 100).map((item) => (
                  <div key={item.id} className="p-3 rounded-lg border bg-gray-50 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium truncate">{item.schedule_name || 'Escala'}</p>
                      <p className="text-xs text-gray-500 truncate">{item.event_name || 'Evento'}</p>
                    </div>
                    <span className="text-xs text-gray-600 shrink-0">{(item.event_date || '').slice(0, 10)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Security */}
        <div className="card">
          <h3 className="font-semibold text-lg mb-4">Segurança</h3>
          
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3">
              <Key size={20} className="text-gray-500" />
              <div>
                <p className="font-medium">Senha</p>
                <p className="text-sm text-gray-500">Altere sua senha de acesso</p>
              </div>
            </div>
            <Button variant="outline" onClick={() => setPasswordDialogOpen(true)}>
              Alterar Senha
            </Button>
          </div>
        </div>

        {/* Permissions */}
        <div className="card">
          <h3 className="font-semibold text-lg mb-4">Minhas Permissões</h3>
          <p className="text-sm text-gray-500 mb-4">
            Você tem {userData?.permissions?.length || 0} permissões ativas baseadas no seu perfil.
          </p>
          
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
            {userData?.permissions?.sort().map(perm => (
              <span key={perm} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded">
                {perm}
              </span>
            ))}
          </div>
        </div>

        {/* Account Info */}
        <div className="card bg-gray-50">
          <h3 className="font-semibold text-lg mb-4">Informações da Conta</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Criado em</p>
              <p className="font-medium">
                {userData?.user?.created_at 
                  ? new Date(userData.user.created_at).toLocaleDateString('pt-BR')
                  : '-'}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Último acesso</p>
              <p className="font-medium">
                {userData?.user?.last_login 
                  ? new Date(userData.user.last_login).toLocaleString('pt-BR')
                  : 'Primeiro acesso'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Change Password Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Senha Atual</Label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                placeholder="Digite sua senha atual"
              />
            </div>
            <div>
              <Label>Nova Senha</Label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            <div>
              <Label>Confirmar Nova Senha</Label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                placeholder="Repita a nova senha"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setPasswordDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleChangePassword} className="btn-primary">
                Alterar Senha
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Profile;
